import { io, Socket } from "socket.io-client";
import { getAuthToken, subscribeAuthToken } from "./auth";
import { logEvent } from "./logging";

// Same-origin: let the browser hit the current host (http://localhost:3000)
const SERVER_URL = (import.meta.env.VITE_SERVER_URL ?? "").trim();
// If SERVER_URL is empty, pass undefined so socket.io uses window.location.origin
const base: string | undefined = SERVER_URL === "" ? undefined : SERVER_URL;

// Try to load any saved user and token from localStorage
function loadAuth() {
    const token = getAuthToken();
    const userRaw = localStorage.getItem("user");
    let user: any = null;
    try { if (userRaw) user = JSON.parse(userRaw); } catch {}
    return { token, user };
}
const { token } = loadAuth();

const SESSION_KEY = "socketSessionId";

function loadSessionId(): string | undefined {
    try { return localStorage.getItem(SESSION_KEY) || undefined; } catch { return undefined; }
}

function persistSessionId(sessionId: string) {
    try { localStorage.setItem(SESSION_KEY, sessionId); } catch {}
}

// Detect simple platform hint for presence (mobile vs web)
const PLATFORM = (() => {
  try {
    const ua = (navigator.userAgent || '').toLowerCase();
    const isCap = typeof (window as any).Capacitor !== 'undefined';
    const isMobile = /android|iphone|ipad|ipod|mobile/i.test(ua) || isCap;
    return isMobile ? 'mobile' : 'web';
  } catch { return 'web'; }
})();

const WEB_SOCKET_ONLY = ["websocket"];
const WEB_SOCKET_WITH_POLLING = ["websocket", "polling"];

export const socket: Socket = io(base, {
    autoConnect: false,
    // Prefer pure WebSocket for lowest latency; skip HTTP polling upgrade
    transports: WEB_SOCKET_ONLY,
    upgrade: false,
    path: "/socket.io",
    withCredentials: true,
    auth: { token: token || undefined, platform: PLATFORM, sessionId: loadSessionId() },
    // Make reconnection more resilient
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 500,
    reconnectionDelayMax: 8000,
    timeout: 12000,
});

subscribeAuthToken((nextToken) => {
    (socket as any).auth = { ...(socket as any).auth, token: nextToken || undefined };
});

let heartbeatTimer: number | undefined;
let consecutiveConnectErrors = 0;
let usingPollingFallback = false;

function startHeartbeat() {
    if (heartbeatTimer) return;
    try {
        heartbeatTimer = window.setInterval(() => {
            try { socket.emit("client:heartbeat", { ts: Date.now() }); } catch {}
        }, 30000);
    } catch {}
}

function stopHeartbeat() {
    if (heartbeatTimer) {
        clearInterval(heartbeatTimer);
        heartbeatTimer = undefined;
    }
}

function enablePollingFallback() {
    if (usingPollingFallback) return;
    usingPollingFallback = true;
    try {
        (socket.io.opts as any).transports = WEB_SOCKET_WITH_POLLING;
        (socket.io.opts as any).upgrade = true;
    } catch {}
}

socket.on("connect", () => {
    consecutiveConnectErrors = 0;
    usingPollingFallback = false;
    try { if (socket.id) persistSessionId(socket.id); } catch {}
    startHeartbeat();
    logEvent("ws:connect", { transport: socket.io.engine?.transport?.name, pollingFallback: usingPollingFallback });
});

socket.on("disconnect", () => {
    stopHeartbeat();
    logEvent("ws:disconnect", { reason: (socket as any)?.reason });
});

socket.on("connect_error", () => {
    consecutiveConnectErrors += 1;
    logEvent("ws:connect_error", { attempts: consecutiveConnectErrors });
    if (consecutiveConnectErrors >= 3) {
        enablePollingFallback();
    }
});

socket.on("reconnect_failed", () => {
    enablePollingFallback();
    logEvent("ws:reconnect_failed", {});
});

// ---- lifecycle helpers ----
export function connectSocket() {
    if (!socket.connected) socket.connect();
}

export function disconnectSocket() {
    if (socket.connected) socket.disconnect();
}

// Ensure quick resume: when the app comes back to foreground, reconnect if needed
if (typeof window !== 'undefined') {
    const tryReconnect = () => {
        if (socket.disconnected) socket.connect();
    };
    try { window.addEventListener('focus', tryReconnect); } catch {}
    try { document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') tryReconnect(); }); } catch {}
}

// ---- local user utils ----
export function getLocalUser() {
    const raw = localStorage.getItem("me");
    try {
        const parsed = raw ? JSON.parse(raw) : null;
        return parsed || {};
    } catch {
        return {};
    }
}

export function setLocalUser(u: any) {
    localStorage.setItem("me", JSON.stringify(u));
}
